import * as React from "react";
import {MassiveActionProcessState, MassiveActionsContext} from "../MassiveActionsProvider";
import {VeboxOverlay} from "../../../components/overlay/VeboxOverlay";
import {ProcessPage, ProcessStageOrder} from "../../Processes";
import {MassiveActionsProcessHeader} from "../components/UtilsComponents";
import {VeboxForm} from "../../../components/form/VeboxForm";
import {CodesValidation} from "../components/CodesValidation";
import {ChooseEquipmentStatus} from "./ChooseEquipmentStatus";
import {CodesCheckAndApprove} from "../components/CodesCheckAndApprove";
import {SelectInputOption} from "@vebox/etui-core";
import {useLocation} from "react-router-dom";
import {ChangeEquipmentStatusContext} from "./ChangeEquipmentStatusProvider";
import {Display} from "../../../components/Display";
import {DATA_STATE} from "../../../users/contexts/UsersDashboardContext";
import {Loader} from "../../../components/Utils/loader/Loader";
import {ValidatedEquipmentInfoModal} from "../components/ValidatedEquipmentInfoModal";
import {MassiveActions, prepareInfoModalItems, prepareMassiveAction} from "../MassiveActionsTypesAndFunctions";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {MassiveOnSubmitModal} from "../components/MassiveOnSubmitModal";
import {DefaultErrorBoundaryFallback} from "../../../ErrorBoundary/DefaultErrorBoundaryFallback";

declare var AppTranslations: AppTranslations;


export const ChangeEquipmentStatus: React.FC<any> = () => {

    const translations = AppTranslations.massiveActions.changeEquipmentStatus;

    const {
        validateCodes,
        validatedEquipmentsInfo,
        executeMassiveAction,
        availableMassivePatchesInfo
    } = React.useContext(MassiveActionsContext);

    const location = useLocation();
    const texts = [translations.changeEquipmentStatusTitle, translations.insertEquipmentCodeListTitle, translations.checkAndConfirmTitle];
    const [values, setValues] = React.useState(undefined);
    const {equipmentsStatuses} = React.useContext(ChangeEquipmentStatusContext);
    const [massiveActionProcessState, setMassiveActionProcessState] = React.useState(
        MassiveActionProcessState.INITIAL);

    const closeModal = () => overlayController.clear()
    const overlayController = VeboxOverlay.useTarget(
        <ValidatedEquipmentInfoModal
            onClose={closeModal}
            title={translations.modalInfoTitle}
            statusElements={prepareInfoModalItems({
                title: translations.validatedEquipmentNotFoundTitle,
                description: translations.validatedEquipmentFoundDescription
            }, {
                title: translations.validatedEquipmentNotFoundTitle,
                description: translations.validatedEquipmentNotFoundDescription
            })}
        />
    );

    const executeAction = (validatedEquipmentGuids: string[]) => {
        setMassiveActionProcessState(MassiveActionProcessState.PENDING);
        const changeEquipmentOperation = values as { changeEquipmentStatus: { status: SelectInputOption } };
        if (changeEquipmentOperation !== undefined) {
            const {value} = changeEquipmentOperation.changeEquipmentStatus.status
            executeMassiveAction(
                prepareMassiveAction(
                    validatedEquipmentGuids,
                    availableMassivePatchesInfo.availableMassivePatches,
                    {
                        valueInfo: {
                            massiveAction: MassiveActions.CHANGE_EQUIPMENT_STATUS,
                            equipmentStatusGuid: value
                        }
                    }))
                .then(() => {
                    overlayController.show(
                        <MassiveOnSubmitModal
                            title={translations.checkAndConfirmSuccessTitle}
                            descriptions={[translations.checkAndConfirmSuccessDescription]}
                            onClose={closeModal}
                            success={true}
                        />
                    )
                    setMassiveActionProcessState(MassiveActionProcessState.INITIAL);
                })
                .catch(manageErrors)
        }
    }

    const manageErrors = (error: { errorType: string, message: string }) => {
        let descriptions: React.ReactNode[] = []
        switch (error.errorType) {
            case 'UNABLE_TO_DELETE_FILE_ON_SFTP':
            case 'UNABLE_TO_UPLOAD_FILE_ON_SFTP':
                descriptions.push(<DefaultErrorBoundaryFallback
                    errorMessage={AppTranslations.errorMessages.unableToPerformTheOperation}
                    style={{backgroundColor: "white"}}
                />)
                break;
            default:
                descriptions.push(<span>{translations.checkAndConfirmErrorDescription}</span>)
                break;
        }
        overlayController.show(
            <MassiveOnSubmitModal
                title={translations.checkAndConfirmErrorTitle}
                descriptions={descriptions}
                onClose={closeModal}
            />)
        setMassiveActionProcessState(MassiveActionProcessState.FAILURE);
    }

    return (
        <ProcessPage total={3} className={"flx-col"}>
            <MassiveActionsProcessHeader
                processHeaderTitle={translations.changeEquipmentStatusTitle}
                processIndicatorsTitles={texts}
            />
            <VeboxOverlay.Target {...overlayController.targetProps} className={"process-stages flx-row"}>
                <VeboxForm onChange={setValues} values={values}>
                    <Display isVisible={equipmentsStatuses && equipmentsStatuses.equipmentsStatusesState === DATA_STATE.loaded}>
                        <ChooseEquipmentStatus
                            title={translations.chooseStatusTitle}
                            cardTitle={translations.chooseEquipmentStatusCardTitle}
                            stepNumber={1}
                            stageOrder={ProcessStageOrder.FIRST}
                            operationDescription={translations.changeEquipmentStatusDescription}
                            operationTitle={translations.changeEquipmentStatusTitle}
                            massiveActionProcessState={massiveActionProcessState}
                        />
                        <CodesValidation
                            validationFunction={validateCodes}
                            stepNumber={2}
                            stageOrder={ProcessStageOrder.MIDDLE}
                            title={translations.insertEquipmentCodeListTitle}
                            cardTitle={translations.codeValidationCardTitle}
                            errorMessage={translations.codeValidationErrorTitle}
                            errorDescription={translations.codeValidationErrorDescription}
                            massiveActionProcessState={massiveActionProcessState}
                        />
                        <CodesCheckAndApprove
                            overlayController={overlayController}
                            submitFunction={executeAction}
                            stepNumber={3}
                            stageOrder={ProcessStageOrder.LAST}
                            title={translations.checkAndConfirmTitle}
                            validatedEquipmentsInfo={validatedEquipmentsInfo}
                            massiveActionProcessState={massiveActionProcessState}
                        />
                    </Display>
                    <Display isVisible={equipmentsStatuses === undefined || equipmentsStatuses.equipmentsStatusesState === DATA_STATE.isLoading}>
                        <Loader/>
                    </Display>
                </VeboxForm>
            </VeboxOverlay.Target>
        </ProcessPage>
    )
}